<nz-spin [nzSpinning]="resource.isLoading()">
  @if (detail(); as d) {
    <!-- Package name + status tag -->
    <div class="mb-4 flex items-center gap-2">
      <span class="text-base font-semibold text-gray-900">
        {{ d.displayName || d.package?.name || '—' }}
      </span>
      <nz-tag
        [nzColor]="
          d.status === 'ACTIVE' ? 'green' :
          d.status === 'USED'   ? 'default' :
          d.status === 'EXPIRED' ? 'orange' : 'red'
        "
      >
        {{ 'servicePackages.status.' + d.status | translate }}
      </nz-tag>
    </div>

    <!-- Info strip: mirrors purchase-edit-modal grid -->
    <div class="grid grid-cols-2 gap-x-6 gap-y-4 mb-4">
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.saleDate' | translate }}</div>
        <div class="text-sm text-gray-800">{{ d.purchasedAt | date: 'dd/MM/yyyy' }}</div>
      </div>
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.soldBy' | translate }}</div>
        <div class="text-sm text-gray-800">{{ d.createdBy?.name || '—' }}</div>
      </div>
      <!-- Owner: the beneficiary who consumes the package (d.customer). Always
           shown; clickable → the shared user-profile drawer. For a self-purchase
           this is the same person as the payer. -->
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.purchases.owner' | translate }}</div>
        @if (d.customer?.id) {
          <a
            class="cursor-pointer text-sm hover:underline"
            (click)="openUser(d.customer?.id, d.customer?.name)"
          >{{ d.customer?.name || '—' }}</a>
        } @else {
          <div class="text-sm text-gray-800">{{ d.customer?.name || '—' }}</div>
        }
      </div>
      <!-- beaut-251: gift — who paid (customer above is the beneficiary).
           Clickable → the payer's user-profile drawer. -->
      @if (d.isGift && d.payer) {
      <div>
        <div class="text-xs text-gray-400">{{ 'services.packages.giftFrom' | translate }}</div>
        <a
          class="cursor-pointer text-sm hover:underline"
          (click)="openUser(d.payer.id, d.payer.name)"
        >{{ d.payer.name }}</a>
      </div>
      }
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.validity' | translate }}</div>
        <div class="whitespace-nowrap text-sm text-gray-800">
          {{ d.validFrom | date: 'dd/MM/yyyy' }} – {{ d.validUntil | date: 'dd/MM/yyyy' }}
        </div>
      </div>
      <div>
        <div class="text-xs text-gray-400">{{ 'servicePackages.editModal.totalPaid' | translate }}</div>
        <div class="text-sm font-semibold text-gray-900">{{ d.purchasePrice | number: '1.2-2' }} ₾</div>
      </div>
    </div>

    <!-- Services list -->
    @if (services().length) {
      <div class="mb-4">
        <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {{ 'servicePackages.editModal.items' | translate }}
        </div>
        <div class="divide-y divide-gray-100">
          @for (s of services(); track s.serviceId) {
            <div class="flex items-baseline justify-between py-2 text-sm">
              <span class="text-gray-800">{{ s.serviceName }}</span>
              <span class="text-gray-600">
                @if (s.originalPrice !== null && s.originalPrice !== undefined && +s.originalPrice !== effectivePrice(s)) {
                  <del class="text-gray-400">{{ s.originalPrice | number: '1.2-2' }}</del>
                }
                {{ effectivePrice(s) | number: '1.2-2' }} ₾ × {{ s.totalQuantity }} =
                <span class="font-medium text-gray-900">{{ lineTotal(s) | number: '1.2-2' }} ₾</span>
              </span>
            </div>
          }
          <div class="flex items-baseline justify-between py-2 text-sm font-semibold">
            <span class="text-gray-800">{{ 'servicePackages.editModal.total' | translate }}</span>
            <span class="text-gray-900">{{ servicesTotal() | number: '1.2-2' }} ₾</span>
          </div>
        </div>
      </div>
    }

    <hr class="mb-4 border-gray-100" />
  }

  <nz-tabset [nzAnimated]="false" (nzSelectedIndexChange)="$event === 1 && onOperationsTab()">
    <nz-tab [nzTitle]="'servicePackages.history.title' | translate">
      <!-- Timeline table (history) -->
      <nz-table
        [nzData]="timeline()"
        nzSize="small"
        [nzFrontPagination]="false"
        [nzShowPagination]="false"
      >
        <thead>
          <tr>
            <th>{{ 'servicePackages.history.when' | translate }}</th>
            <th>{{ 'servicePackages.history.kind' | translate }}</th>
            <th>{{ 'servicePackages.history.who' | translate }}</th>
            <th>{{ 'servicePackages.history.details' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (row of timeline(); track $index) {
            <tr>
              <td>{{ row.date | date: 'dd/MM/yyyy HH:mm' }}</td>
              <td>
                <nz-tag
                  [nzColor]="
                    row.kind === 'adjustment' ? 'orange' :
                    row.kind === 'audit'      ? 'blue'   :
                    row.kind === 'purchase'   ? '#722ed1' : 'green'
                  "
                >
                  {{ ('servicePackages.history.kinds.' + row.kind) | translate }}
                </nz-tag>
              </td>
              <td>
                @if (row.kind === 'audit' || row.kind === 'adjustment') {
                  {{ row.payload.editor?.fullName ?? row.payload.editorId ?? '—' }}
                } @else if (row.kind === 'purchase') {
                  {{ row.payload.soldBy ?? '—' }}
                } @else {
                  <!-- beaut-376: usage rows previously always rendered a dash.
                       Show the employee who performed the service, matching the
                       column's meaning on the other row types (who did this). -->
                  {{ row.payload.employee?.name ?? '—' }}
                }
              </td>
              <td>
                @if (row.kind === 'audit') {
                  <span>
                    {{ fieldLabelKey(row.payload.fieldChanged) | translate }}:
                    @if (row.payload.fieldChanged === 'added') {
                      {{ formatAdded(row.payload.newValue) }}
                    } @else {
                      {{ formatValue(row.payload.fieldChanged, row.payload.oldValue) }} → {{ formatValue(row.payload.fieldChanged, row.payload.newValue) }}
                    }
                  </span>
                } @else if (row.kind === 'adjustment') {
                  <span>
                    {{ row.payload.oldDiscountedPrice }} → {{ row.payload.newDiscountedPrice }} ₾,
                    {{ 'servicePackages.editModal.balanceChange' | translate }}:
                    {{ (+row.payload.diffAmount > 0 ? '+' : '') }}{{ row.payload.diffAmount }} ₾,
                    {{ reasonLabel(row.payload.reason) }}
                  </span>
                } @else if (row.kind === 'purchase') {
                  <span>{{ row.payload.price | number: '1.2-2' }} ₾</span>
                } @else {
                  <span>{{ 'servicePackages.history.usage' | translate }} × {{ row.payload.quantityUsed }}</span>
                  <!-- beaut-376: name the consumer (the package beneficiary) on
                       each usage row. On a gift the payer and consumer differ,
                       and QA needs to see who actually used it without reading
                       it off the header. -->
                  @if (detail()?.customer?.name) {
                    <span class="text-gray-500"> · {{ detail()?.customer?.name }}</span>
                  }
                }
              </td>
            </tr>
          }
        </tbody>
      </nz-table>
    </nz-tab>
    <nz-tab [nzTitle]="'servicePackages.operations.title' | translate">
      <nz-spin [nzSpinning]="operationsLoading()">
        @if (operations()?.length) {
          <nz-table [nzData]="operations()!" nzSize="small" [nzFrontPagination]="false" [nzShowPagination]="false">
            <thead>
              <tr>
                <th>{{ 'servicePackages.history.when' | translate }}</th>
                <th>#</th>
                <th>{{ 'servicePackages.operations.source' | translate }}</th>
                <th>{{ 'servicePackages.operations.amount' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (op of operations()!; track op.id) {
                <tr class="cursor-pointer" (click)="openOperation(op.id)">
                  <td>{{ op.operationDate | date: 'dd/MM/yyyy HH:mm' }}</td>
                  <td>{{ op.id }}</td>
                  <td>{{ 'OPERATION_SOURCE.' + op.operationSource | translate }}</td>
                  <td>{{ op.price | number: '1.2-2' }} ₾</td>
                </tr>
              }
            </tbody>
          </nz-table>
        } @else if (operations() !== null) {
          <div class="py-4 text-center text-gray-400">—</div>
        }
      </nz-spin>
    </nz-tab>
  </nz-tabset>
</nz-spin>
